import { type MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import * as React from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import { ImageModal } from '#app/components/image-modal.tsx'
import { LandingPopup } from '#app/components/landing-popup.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { ChromeExtension } from './sections/chrome-extension.tsx'
import { FAQS } from './sections/faq.tsx'
import { Showcase } from './sections/showcase.tsx'
import { Title } from './sections/utils.tsx'

let siteDescription =
	'Create funny, weird and amazing face swaps with any picture. No Photosip skills required. Our AI will do the magic for you'

let ogImage = 'https://swapmyface.app/img/og-image.jpg'
export const meta: MetaFunction = () => [
	{ title: 'SwapMyFace: Face Swap Online - AI Face Swapper' },
	{
		name: 'twitter:card',
		content: 'summary_large_image',
	},
	{
		name: 'twitter:site',
		content: '@swapmyface',
	},
	{
		name: 'twitter:creator',
		content: '@swapmyface',
	},
	{
		name: 'twitter:title',
		content: 'SwapMyFace AI: Put your face in any picture, in 5 seconds',
	},
	{
		name: 'twitter:description',
		content: siteDescription,
	},
	{
		name: 'twitter:image',
		content: ogImage,
	},
	{
		property: 'og:image',
		content: ogImage,
	},
	{ property: 'og:url', content: 'https://swapmyface.app' },
	{ property: 'og:type', content: 'website' },
	{
		property: 'og:title',
		content: 'SwapMyFace: Face Swap Online - AI Face Swapper',
	},
	{
		name: 'robots',
		content: 'max-image-preview:large',
	},
]

const quotes = [
	{
		text: "... Showed my friends and they couldn't believe it. Best face-swapping app out there. Want to feel like a superhero? Try this!",
		author: 'Sara W.',
		img: '/img/quotes/1.png',
		imgAlt: 'face swap of a woman with a photo of wonder woman',
		big_img: '/img/quotes/1_big.png',
	},
	{
		text: 'Swapped my face onto The Witcher with SwapMyFace. Crazy good! 😎 My friends thought I was in the series. Best app for a quick fantasy trip!',
		author: 'Johnny D.',
		img: '/img/quotes/2.png',
		imgAlt: 'face swap of a man with a photo of gerald, the witcher',
		big_img: '/img/quotes/2_big.png',
	},
]

type QuoteType = (typeof quotes)[number]

export default function Index() {
	return (
		<div>
			<ClientOnly>
				{() => (
					<LandingPopup
						message="%s face swaps generated in the last minute"
						delayTime={5_000}
					/>
				)}
			</ClientOnly>
			<Landing />
			<Showcase />
			<ChromeExtension />
			<HowItWorks />
			<FAQS />
			<MarketingSecondary />
		</div>
	)
}

function Landing() {
	const user = useOptionalUser()

	return (
		<main className="bg-landing-image bg-cover md:bg-contain">
			<Spacer size="4xs" />
			<div className="h-20 md:h-36" />
			<div className="container flex items-center justify-center gap-2 pl-8">
				<div>
					<h1 className="flex flex-col justify-start text-h2 sm:flex-row md:text-h1">
						<span>Put your face in</span>{' '}
						<div className="hidden sm:block">&nbsp;</div>
						<div className="flex flex-row">
							<div className="flex flex-col ">
								<span>ANY</span>
								<SubIcon className="mt-[-18px] w-20 md:w-28" />
							</div>
							&nbsp;
							<span>picture, in 5 seconds!</span>
						</div>
					</h1>
					<h2 className="m-0 mt-3 text-body-sm font-normal md:mt-[-5px]">
						Create funny, weird, and amazing face swaps with anyone, anywhere.
						No Photoshop skills required. Our AI will do the magic for you.
					</h2>
					<div className="h-12 md:h-28" />
					<div className="flex flex-col">
						<div className="block">
							<Link
								prefetch="viewport"
								to={user ? '/me' : '/signup'}
								className="bg-app-gradient text-balance rounded-xl px-8 py-3 text-h5 md:text-h3"
							>
								<span>{user ? 'Go to the app' : 'Start Swapping Faces'}</span>
							</Link>
						</div>
						<div className="h-5" />
						<div className="flex flex-row items-center gap-2">
							<FiveStars />
							<span className="mt-1 text-sm">
								700K pictures generated so far
							</span>
						</div>
					</div>
					<Spacer size="sm" />
					<div className="flex flex-col justify-between gap-10 md:flex-row md:gap-20">
						{quotes.map((quote, i) => (
							<Quote quote={quote} key={i} i={i} />
						))}
					</div>
				</div>
			</div>
			<Spacer size="xs" />
			<div className="flex flex-row justify-center">
				<div className="rounded-xl bg-yellow-400 px-12 py-3 text-2xl text-black">
					<b>No monthly subscription</b>
				</div>
			</div>
			<Spacer size="4xs" />
		</main>
	)
}

function HowItWorks() {
	return (
		<div className="container">
			<Spacer size="sm" />
			<div className="flex flex-col gap-1">
				<Title>How It Works</Title>
				<span className="hidden text-sm opacity-30">
					You will have 5 free generations so you can try it out before you put
					your credit card.
				</span>
			</div>
			<Spacer size="2xs" />
			<div className="flex flex-col gap-4 text-2xl">
				<span>1- Upload your picture</span>
				<span>2- Upload the picture where you want to put your face in</span>
				<span>3- Click the button to merge them</span>
				<span>You are done!</span>
			</div>
			<Spacer size="2xs" />
			<div className="flex flex-row gap-1 text-sm opacity-30">
				<Icon name="info" className="h-4 w-4" />
				We automatically save the link to the images you generate, so you have
				access to them. They will be available all the time for you.
			</div>
			<Spacer size="sm" />
		</div>
	)
}

function Quote({ quote, i }: { quote: QuoteType; i: number }) {
	const [open, setOpen] = React.useState<string | null>()
	let gradient = 'from-[#00000066] to-[#fc466b24]'
	let gradient2 = 'from-[#00000066] to-[#3f5efb24]'

	return (
		<div
			className={cn(
				'flex flex-col justify-start gap-8 bg-gradient-to-b p-8 sm:flex-row',
				i % 2 == 0 ? gradient : gradient2,
			)}
			style={{
				borderRadius: '14px',
				border: '1px solid #1f1f1f',
				backgroundBlendMode: 'normal',
				maxHeight: '335px',
			}}
		>
			<img
				onClick={() => setOpen(quote.big_img)}
				loading="lazy"
				alt={quote.imgAlt}
				src={quote.img}
				className="h-auto w-40 flex-[2] transform self-center transition duration-200 ease-in-out hover:scale-105 hover:cursor-pointer md:self-start"
			/>
			<div className="flex flex-[3] flex-col gap-4">
				<div className="mt-2 text-sm">{quote.text}</div>
				<div className="mt-2 flex h-full self-end text-sm">
					<span className="self-end">{quote.author}</span>
				</div>
			</div>
			{open && <ImageModal src={open} onClose={() => setOpen(null)} />}
		</div>
	)
}

function FiveStars() {
	return (
		<div className="mt-1 flex flex-row gap-1">
			{Array.from({ length: 5 }).map((_, i) => (
				<img
					className="h-4 w-4"
					src="/img/star.png"
					key={i}
					alt={`star no. ${i}+1`}
				/>
			))}
		</div>
	)
}

function SubIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			style={{ transform: 'scaleX(-1) rotate(5deg)' }}
			width="155"
			height="37"
			viewBox="0 0 155 37"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M78 19H4.5L8.03884 22.0965C28.4339 39.9422 59.2613 38.5778 78 19V19ZM78 19H151.5V19C133.7 -2.30619 101.528 -3.94703 81.6525 15.4376L78 19Z"
				stroke="url(#paint0_linear_3_6)"
				strokeWidth="3"
			/>
			<defs>
				<linearGradient
					id="paint0_linear_3_6"
					x1="-6.99999"
					y1="1.49995"
					x2="169"
					y2="27.9999"
					gradientUnits="userSpaceOnUse"
				>
					<stop offset="0.0121552" stopColor="#FC466B" />
					<stop offset="1" stopColor="#3F5EFB" />
				</linearGradient>
			</defs>
		</svg>
	)
}

function MarketingSecondary() {
	return (
		<section id="marketing2">
			<div className="h-12 md:h-20" />
			<div className="container">
				<div className="flex flex-col md:flex-row">
					<div className="flex flex-[3] flex-col">
						<h2 className="text-h1">Put your face in ANY picture</h2>
						<div>
							It only takes 5 seconds to generate a picture of YOUR face with
							any other picture you want. A movie character? A historic figure?
							A famous painting? Anything is possible.
						</div>
						<div className="h-12 md:h-24" />
						<div>
							<Link
								prefetch="viewport"
								to="/login"
								className="bg-app-gradient block w-fit rounded-xl px-8 py-3 text-base md:text-h5"
							>
								Start putting your face in any picture
							</Link>
						</div>
					</div>
					<div className="md:flex md:flex-[2]">
						<div className="h-12 md:block md:h-0 md:w-20" />
						<div>
							<img
								src="/img/t1.png"
								className="md:mt-[-10px]"
								alt={
									'Showcase of generation of images, from left to right, a photo of Jack Gylenhall, ' +
									'a photo of Cylian Murphy and a photo of the body of Cylian with the face of Jack'
								}
							/>
						</div>
						<div className="hidden md:block md:w-20" />
					</div>
				</div>
			</div>
			<div className="hidden md:h-20" />
		</section>
	)
}
